import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import Sidebar from './components/Sidebar';
import Nav from './components/Nav';
// import MobileEmailFooter from './components/MobileEmailFooter';
import Home from './pages/Home';
import About from './pages/About';
import Photographs from './pages/Photographs';
import Explore from './pages/Explore';
import GSL from './pages/subpages/GSL';
import Espresso from './pages/subpages/Espresso';
import FourRunner from './pages/subpages/4runner';
import Tattoo from './pages/subpages/Tattoo';
import DatePickerSandbox from './pages/DatePickerSandbox';
import NotFound from './pages/NotFound';
import ExploreSubNav from './components/ExploreSubNav';
import { setMetaTags, findFirstImage } from './utils/metaTags';
import { routeMetaConfig } from './config/routes';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { getPageTheme, getThemeClasses } from './utils/theme';
// import { activeEmailConfig } from './config/emailConfig';

// Add this function at the top level, outside of any component
function scrollToHashElement() {
  // Check if there's a hash in the URL
  if (window.location.hash) {
    // Get the target element
    const hash = window.location.hash.substring(1);
    const element = document.getElementById(hash);
    
    // If the element exists, scroll to it
    if (element) {
      // Use setTimeout to ensure the DOM is fully loaded
      setTimeout(() => {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 144;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }, 300);
    }
  }
}

const AppContent: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const contentRef = useRef<HTMLDivElement>(null);
    const [imagesLoaded, setImagesLoaded] = React.useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    const { theme } = useTheme();
    // const [showMobileEmailFooter, setShowMobileEmailFooter] = useState(false);

    const themeClasses = getThemeClasses(theme);
    const currentTheme = getPageTheme(location.pathname, theme);
    const isExplore = location.pathname.startsWith('/explore');
    const showHamburger = isExplore && scrolled;
    const showPrimaryNav = !isExplore || !scrolled || navOpen;

    // Collapse primary nav on explore so name + explore subnav stick together
    useEffect(() => {
        const handleScroll = () => {
            const next = window.scrollY > 12;
            setScrolled(next);
            if (!next) setNavOpen(false);
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setScrolled(window.scrollY > 12);
        setNavOpen(false);
    }, [location.pathname]);

    // Handle redirects
    useEffect(() => {
        if (location.pathname === '/news') {
            navigate('/', { replace: true });
        }
    }, [location.pathname, navigate]);

    // Handle hash links on page load and route changes
    useEffect(() => {
        scrollToHashElement();
    }, [location.pathname, location.hash]);

    // Handle meta tags
    useEffect(() => {
        // Function to update meta tags based on current route
        const updateMetaTags = () => {
            // Get meta config for current route, or use default if not found
            const path = location.pathname === '/' ? '/' : location.pathname;
            const metaConfig = routeMetaConfig[path] || routeMetaConfig['/'];
            
            // Wait a bit to ensure images are loaded
            setTimeout(() => {
                // Use configured image first, then try to find first image in content
                let imageUrl = metaConfig.image;
                if (!imageUrl) {
                    imageUrl = findFirstImage(contentRef);
                }
                
                // Set meta tags
                setMetaTags({
                    title: metaConfig.title,
                    description: metaConfig.description,
                    imageUrl: imageUrl,
                    url: window.location.href
                });
            }, 500);
        };

        // Update meta tags when route changes
        updateMetaTags();
        
        // Also update when images are loaded
        if (imagesLoaded) {
            updateMetaTags();
        }

        // Add event listener for when all images are loaded
        const handleLoad = () => {
            if (document.readyState === 'complete') {
                setImagesLoaded(true);
            }
        };

        window.addEventListener('load', handleLoad);
        return () => window.removeEventListener('load', handleLoad);
    }, [location.pathname, imagesLoaded]);

    // useEffect(() => {
    //     if (showMobileEmailFooter) {
    //         document.body.classList.add('overflow-hidden')
    //     } else {
    //         document.body.classList.remove('overflow-hidden')
    //     }
    //     return () => {
    //         document.body.classList.remove('overflow-hidden')
    //     }
    // }, [showMobileEmailFooter])

    return (
        <div ref={contentRef} className={`min-h-screen ${themeClasses} main-content ${currentTheme === 'dark' ? 'dark-theme-container' : ''}`}>
            {/* Mobile Header — name matches subnav height; on explore scroll, name + subnav stick together */}
            <div className="md:hidden sticky top-0 z-50">
                <div className="flex items-center justify-between gap-x-3 py-3 px-4 bg-white">
                    <Link to="/" className="text-black">
                        Taylor Pemberton
                    </Link>
                    {showHamburger && (
                        <button
                            type="button"
                            aria-label={navOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={navOpen}
                            onClick={() => setNavOpen((open) => !open)}
                            className="relative flex h-[16px] w-[18px] flex-col justify-center gap-[4px]"
                        >
                            <span
                                className={clsx(
                                    'block h-[2px] w-[18px] origin-center bg-black transition duration-150 ease-out',
                                    navOpen && 'translate-y-[6px] rotate-45',
                                )}
                            />
                            <span
                                className={clsx(
                                    'block h-[2px] w-[18px] bg-black transition duration-150 ease-out',
                                    navOpen && 'opacity-0',
                                )}
                            />
                            <span
                                className={clsx(
                                    'block h-[2px] w-[18px] origin-center bg-black transition duration-150 ease-out',
                                    navOpen && '-translate-y-[6px] -rotate-45',
                                )}
                            />
                        </button>
                    )}
                </div>
                <div
                    className={clsx(
                        'overflow-hidden transition-[max-height,opacity] duration-150 ease-out',
                        showPrimaryNav ? 'max-h-14 opacity-100' : 'max-h-0 opacity-0',
                        navOpen && 'bg-black/80',
                    )}
                >
                    <Nav />
                </div>
                {isExplore && <ExploreSubNav />}
            </div>

            {/* Desktop Sidebar */}
            <div className={`hidden md:block fixed inset-y-0 left-0 w-[288px] z-10 ${theme === 'light' ? 'bg-white' : 'bg-black'}`}>
                <Sidebar />
            </div>

            {/* Main Content */}
            <main className={`md:pl-[288px] ${themeClasses} relative z-5`}>
                {/* Desktop Nav */}
                <div className="hidden md:block sticky top-0 z-40">
                    <Nav />
                </div>

                {/* Page Content */}
                <div className={`w-full ${currentTheme === 'light' ? 'bg-white' : ''}`}>
                    <div className="px-4 pt-28 -mt-24 md:pt-32 pb-32 md:pb-20 max-w-[652px]">
                        <Routes>
                            <Route path="/" element={<Home theme={getPageTheme('/', theme)} />} />
                            <Route path="/about" element={<About theme={getPageTheme('/about', theme)} />} />
                            <Route path="/photographs" element={<Photographs theme={getPageTheme('/photographs', theme)} />} />
                            <Route path="/explore" element={<Explore theme={getPageTheme('/explore', theme)} />} />
                            <Route path="/explore/gsl" element={<GSL theme={getPageTheme('/explore/gsl', theme)} />} />
                            <Route path="/explore/espresso" element={<Espresso theme={getPageTheme('/explore/espresso', theme)} />} />
                            <Route path="/explore/tattoo" element={<Tattoo theme={getPageTheme('/explore/tattoo', theme)} />} />
                            <Route path="/explore/date-picker-sandbox" element={<DatePickerSandbox theme={getPageTheme('/explore/date-picker-sandbox', theme)} />} />
                            <Route path="/explore/condo-renovation" element={<GSL theme={getPageTheme('/explore/gsl', theme)} />} />
                            <Route path="/explore/4runner" element={<FourRunner theme={getPageTheme('/explore/4runner', theme)} />} />
                            <Route path="/news" element={<NewsRedirect />} />
                            <Route path="*" element={<NotFound theme={getPageTheme('/', theme)} />} />
                        </Routes>
                    </div>
                </div>
            </main>

            {/* <MobileEmailFooter 
                theme={theme} 
                serviceConfig={activeEmailConfig}
                active={showMobileEmailFooter} 
                onClose={() => setShowMobileEmailFooter(false)} 
            /> */}
        </div>
    );
};

const NewsRedirect: React.FC = () => {
    const navigate = useNavigate();

    useEffect(() => {
        navigate('/', { replace: true });
    }, [navigate]);

    return <p>Redirecting...</p>;
};

const App: React.FC = () => {
    return (
        <div className="main-content">
            <BrowserRouter>
                <ThemeProvider>
                    <AppContent />
                </ThemeProvider>
            </BrowserRouter>
        </div>
    );
};

export default App; 